import React from 'react'
// components
import TableToolbar from './components/TableToolbar'
import TableHeader, { THeader } from './components/TableHeader'
import { LoaderWithOverlay } from 'components/UI'
import { Table as MaterialTable, TableBody, TablePagination } from '@mui/material'
// constants
import { PER_PAGE_OPTIONS } from 'constants/table'
// views
import { TableWrapper } from 'components/UI/Table/views'

const SLOP_PROPS = {
  actions: {
    nextButton: { 'aria-label': 'Next Page' },
    previousButton: { 'aria-label': 'Previous Page' }
  }
}

interface Props<T> {
  data: T[]
  page?: number
  title?: string
  headers: THeader[]
  refetch?: (...args: any) => void
  perPage?: number
  filters?: string
  children: (props: { data: T[] }) => React.ReactNode
  isLoading?: boolean
  totalCount: number
  initialOrder?: 'desc' | 'asc'
  onPageChange?: (values: number) => void
  initialOrderBy?: string
  onPerPageChange?: (value: number) => void
}

const Table = <T extends {}>({
  data,
  page,
  title,
  perPage,
  refetch,
  filters,
  headers,
  children,
  isLoading,
  totalCount,
  initialOrder,
  onPageChange,
  initialOrderBy,
  onPerPageChange
}: Props<T>) => {
  const [order, setOrder] = React.useState<'desc' | 'asc'>(initialOrder || 'desc')
  const [orderBy, setOrderBy] = React.useState(initialOrderBy || null)

  const handleSortChange = (newOrderBy: 'desc' | 'asc') => () => {
    if (orderBy === newOrderBy) {
      setOrder(order === 'asc' ? 'desc' : 'asc')
    } else {
      setOrder('desc')
      setOrderBy(newOrderBy)
    }
  }

  const handlePageChange = (_: React.MouseEvent, page: number) => {
    onPageChange?.(page)
  }

  const handlePerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageChange?.(0)
    onPerPageChange?.(parseInt(event.target.value))
  }

  React.useEffect(() => {
    if (orderBy) {
      refetch?.({ order: { field: orderBy, direction: order.toUpperCase() } })
    }
  }, [order, orderBy])

  return (
    <TableWrapper elevation={3} isLoading={isLoading}>
      {title && <TableToolbar title={title}>{filters}</TableToolbar>}
      <MaterialTable aria-labelledby='tableTitle' stickyHeader>
        <TableHeader headers={headers} order={order} orderBy={orderBy} handleSortChange={handleSortChange} />
        <TableBody>
          {isLoading ? <LoaderWithOverlay isLoading={isLoading} height='100%' /> : <>{children({ data })}</>}
        </TableBody>
      </MaterialTable>
      <TablePagination
        page={page}
        component='div'
        rowsPerPage={perPage}
        slotProps={SLOP_PROPS}
        count={totalCount || 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePerPage}
        rowsPerPageOptions={PER_PAGE_OPTIONS}
      />
    </TableWrapper>
  )
}

export default Table
